import React, { useState, useMemo } from 'react';
import './css/Product.css';
import { CATEGORIES, getProducts } from '../lib/productData.js';
import { useNavigate } from 'react-router-dom';
import MobileReviewDeck from './MobileReviewDeck.jsx';
import BannerImage from './BannerImage.jsx';

const INITIAL_PRODUCTS = getProducts();

const REVIEWS = [
  { id: 1, name: "Rajesh Kumar", role: "Fleet Procurement Manager", stars: 5, text: "The structural build quality of these axle nuts and wheel spindles is exceptional. Zero tolerance issues across bulk deployments.", featured: true },
  { id: 2, name: "Vikram Singh", role: "Automotive Service Lead", stars: 5, text: "Switched to Walnut for our workshop return spring kits and split pins. Outstanding corrosive resistance under stress testing.", featured: false },
  { id: 3, name: "Arjun Mehta", role: "Operations Director, MotoCraft Assemblies", stars: 5, text: "The high-tensile allen bolts and custom fairing clips from Walnut have significantly reduced our rework rates. Flawless threading across every batch.", featured: false }
];

export default function Products() {
  const [activeCategory, setActiveCategory] = useState("All Products");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("Sort: Featured");
  const [lightbox, setLightbox] = useState(null); // { image, name, meta, category }
  const navigate = useNavigate();

  const filteredProducts = useMemo(() => {
    let result = [...INITIAL_PRODUCTS];
    if (activeCategory !== "All Products") {
      result = result.filter(product => product.category === activeCategory);
    }
    if (searchQuery.trim() !== "") {
      const targetQuery = searchQuery.toLowerCase();
      result = result.filter(product =>
        product.name.toLowerCase().includes(targetQuery) ||
        product.meta.toLowerCase().includes(targetQuery)
      );
    }
    if (sortBy === "Name: A to Z") result.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === "Name: Z to A") result.sort((a, b) => b.name.localeCompare(a.name));
    else if (sortBy === "Newest First") result.sort((a, b) => (b.badge === "NEW" ? 1 : 0) - (a.badge === "NEW" ? 1 : 0));
    return result;
  }, [activeCategory, searchQuery, sortBy]);

  // Close on Escape key
  React.useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div className="walnut-products-view-wrapper">

      {/* ── LIGHTBOX MODAL ── */}
      {lightbox && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightbox(null)}
        >
          <div
            className="lightbox-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              className="lightbox-close"
              onClick={() => setLightbox(null)}
            >
              ✕
            </button>

            {/* Image */}
            <div className="lightbox-img-wrap">
              <img
                src={`/resources/${lightbox.image}`}
                alt={lightbox.name}
                className="lightbox-img"
              />
            </div>

            {/* Info */}
            <div className="lightbox-info">
              {lightbox.badge && (
                <span className="lightbox-badge">{lightbox.badge}</span>
              )}
              <span className="lightbox-category">{lightbox.category}</span>
              <h3 className="lightbox-name">{lightbox.name}</h3>
              <p className="lightbox-meta">{lightbox.meta}</p>
              <button
                className="lightbox-cta"
                onClick={() => { setLightbox(null); navigate('/contact'); }}
              >
                Inquire for Specs →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── HERO BANNER ── */}
      <section className="products-hero-banner" style={{ width: '100%', backgroundColor: '#0E0E0E', overflow: 'hidden' }}>
        <BannerImage
          src="/banners/PRODUCT PAGE WALNUT.png"
          alt="Walnut Industries Fasteners Catalog Showcase"
          style={{ width: '100%' }}
          imageStyle={{ height: 'auto' }}
          fallbackText="Products Banner"
        />
      </section>

      {/* ── VALUE STRIP ── */}
      <div className="red-value-strip">
        <div className="rv-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          Reliable Performance
        </div>
        <div className="rv-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          Corrosion Resistant
        </div>
        <div className="rv-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          Tested for Safety
        </div>
        <div className="rv-item">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/></svg>
          Comprehensive Inventory
        </div>
      </div>

      {/* ── FILTER BAR ── */}
      <div className="filter-dashboard-bar">
        <div className="filter-tabs-group">
          {CATEGORIES.map(category => (
            <button
              key={category}
              className={`filter-tab-btn ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
        <div className="filter-inputs-group">
          <select
            className="sort-dropdown-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option>Sort: Featured</option>
            <option>Name: A to Z</option>
            <option>Name: Z to A</option>
            <option>Newest First</option>
          </select>
          <div className="search-input-field-container">
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="search-embedded-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
          </div>
        </div>
      </div>

      {/* ── PRODUCTS GRID ── */}
      <main className="products-grid-section-container">
        <div className="grid-layout-header">
          <h2 className="grid-layout-title">{activeCategory} ({filteredProducts.length})</h2>
          {(activeCategory !== "All Products" || searchQuery !== "") && (
            <button
              className="grid-reset-btn"
              onClick={() => { setActiveCategory("All Products"); setSearchQuery(""); setSortBy("Sort: Featured"); }}
            >
              Reset Filters →
            </button>
          )}
        </div>

        {filteredProducts.length === 0 ? (
          <div className="empty-results-fallback">
            <h3>No matching products found</h3>
            <p>Try modifying your search or filters.</p>
          </div>
        ) : (
          <div className="products-catalogue-grid">
            {filteredProducts.map(product => (
              <article className="product-item-card" key={product.id}>
                <div
                  className="product-thumbnail-area"
                  onClick={() => setLightbox(product)}
                  style={{ cursor: 'zoom-in' }}
                >
                  {product.badge && <span className="product-ribbon-badge">{product.badge}</span>}

                  <img
                    src={`/resources/${product.image}`}
                    alt={product.name}
                    height="160px"
                    className="product-card-thumbnail-img"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />

                  <svg className="fallback-card-vector" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" style={{ display: 'none' }}>
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18M9 21V9" />
                  </svg>

                  {/* Zoom hint on hover */}
                  <div className="thumbnail-zoom-hint">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
                    </svg>
                  </div>

                  <span className="thumbnail-category-label">{product.category}</span>
                </div>

                <div className="product-details-area">
                  <h3 className="product-card-title">{product.name}</h3>
                  <p className="product-card-metadata">{product.meta}</p>
                  <div className="product-card-footer-row">
                    <button onClick={() => navigate('/contact')} className="product-card-action-btn unique-inquiry-btn">
                      Inquire for Specs
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      {/* ── CUSTOMER REVIEWS ── */}
      <section className="client-endorsements-section">
        <div className="centered-section-heading"><h2>CUSTOMER REVIEW</h2></div>
        <div className="endorsements-cards-container desktop-reviews">
          {REVIEWS.slice(0, 3).map((review, idx) => {
            let positionClass = "card-center";
            if (idx === 0) positionClass = "card-left";
            if (idx === 2) positionClass = "card-right";
            return (
              <div className={`endorsement-card-v2 ${positionClass} ${review.featured ? 'highlighted' : ''}`} key={review.id}>
                <div className="avatar-circle-container">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
                <h3 className="endorser-identity-v2">{review.name}</h3>
                <div className="star-rating-row-v2">{"★".repeat(review.stars)}</div>
                <p className="endorsement-quote-text-v2">"{review.text}"</p>
              </div>
            );
          })}
        </div>
        <MobileReviewDeck reviews={REVIEWS.slice(0, 3)} />
      </section>

      {/* ── VEHICLE COMPATIBILITY ── */}
      <section className="vehicle-compatibility-section">
        <div className="centered-section-heading"><h2>Compatible Vehicle Models</h2></div>
        <div style={{ padding: '0 40px 20px' }}>
          <div className="brands-grid">
            {[
              { name: "Hero MotoCorp", image: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0f/Hero_MotoCorp.svg/1280px-Hero_MotoCorp.svg.png" },
              { name: "Bajaj Auto", image: "https://1000logos.net/wp-content/uploads/2020/07/Bajaj-logo.jpg" },
              { name: "TVS Motor", image: "https://onlykutts.com/wp-content/uploads/2022/08/TVS-new-logo.jpeg" },
              { name: "Honda", image: "https://upload.wikimedia.org/wikipedia/commons/7/7b/Honda_Logo.svg" },
              { name: "Royal Enfield", image: "https://images.seeklogo.com/logo-png/36/1/royal-enfield-logo-png_seeklogo-361484.png" },
              { name: "Yamaha", image: "https://img2.clipart-library.com/26/yamaha-logo-clipart/yamaha-logo-clipart-3.webp" },
            ].map((brand, idx) => (
              <div className="brand-card" key={idx}>
                <img
                  src={brand.image} alt={brand.name}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '12px', filter: 'grayscale(100%)', transition: 'filter 0.3s' }}
                  onMouseEnter={e => e.currentTarget.style.filter = 'grayscale(0%)'}
                  onMouseLeave={e => e.currentTarget.style.filter = 'grayscale(100%)'}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}